import { Fragment, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { prefersReducedHeroMotion } from "@/components/sections/hero/useHeroSplineVisibility";
import { HERO_AI_BAR_ENABLED } from "@/components/sections/hero/heroShared";
import HeroAiConsultBar from "@/components/sections/hero/HeroAiConsultBar";
import MainHeroVideo from "@/components/sections/mainHero/MainHeroVideo";
import { useMainHeroScrollProgress } from "@/components/sections/useMainHeroScrollProgress";
import { useMainHeroMoonLight } from "@/components/sections/useMainHeroMoonLight";
import { useCms } from "@/lib/cms";
import { resolveMainHero } from "@/lib/siteContent";
import "@/components/sections/HeroSection.css";
import "./MainHeroSection.css";

function HeroCtaBlock({ copy, lang, testId, opacityVar, href }) {
  return (
    <div
      lang={lang}
      className="ds-main-hero-section__cta"
      data-testid={testId}
      style={{ opacity: `var(${opacityVar}, 1)` }}
    >
      <p className="ds-main-hero-section__cta-lead">{copy.lead}</p>
      <Link to={href} className="ds-main-hero-section__cta-btn">
        {copy.label}
        <ArrowRight size={16} aria-hidden="true" />
      </Link>
    </div>
  );
}

function HeroTagline({ items, className, testId, opacityVar }) {
  return (
    <p
      data-testid={testId}
      className={className}
      style={{ opacity: `var(${opacityVar}, 1)` }}
    >
      {items.map((item, index) => (
        <Fragment key={item}>
          {index > 0 ? <span aria-hidden="true">•</span> : null}
          {item}
        </Fragment>
      ))}
    </p>
  );
}

function HeroTitleBlock({ copy, lang, className, highlightTestId, testId, opacityVar }) {
  return (
    <div
      lang={lang}
      aria-hidden="true"
      data-testid={testId}
      className={className}
      style={{ opacity: `var(${opacityVar}, 1)` }}
    >
      <span className="ds-main-hero-section__title-line">{copy.line1}</span>
      <span className="ds-main-hero-section__title-line ds-main-hero-section__title-line--accent">
        {copy.line2Before}
        <span data-testid={highlightTestId}>{copy.line2Highlight}</span>
        {copy.line2After || null}
      </span>
    </div>
  );
}

export default function MainHeroSection() {
  const { cms } = useCms();
  const hero = resolveMainHero(cms);
  const trackRef = useRef(null);
  const stickyRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(() => prefersReducedHeroMotion());
  const scrollEnabled = !reduceMotion;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useMainHeroScrollProgress(trackRef, scrollEnabled);
  useMainHeroMoonLight(stickyRef, !reduceMotion);

  const setSectionRef = (node) => {
    trackRef.current = node;
  };

  return (
    <section
      id="hero"
      data-testid="main-hero-section"
      className={`ds-main-hero-section${reduceMotion ? " ds-main-hero-section--reduce-motion" : ""}`}
      aria-label="Home hero"
    >
      <div
        ref={setSectionRef}
        className="ds-main-hero-section__track"
        data-testid="main-hero-scroll-track"
      >
        <div
          ref={stickyRef}
          className="ds-main-hero-section__sticky"
        >
          <div className="ds-main-hero-section__media" aria-hidden="true">
            <MainHeroVideo autoplay={!reduceMotion} />
            <div className="ds-main-hero-section__scrim" />
          </div>

          <div className="ds-main-hero-section__moon-light" aria-hidden="true">
            <div className="ds-main-hero-section__moon-beam ds-main-hero-section__moon-beam--core" />
            <div className="ds-main-hero-section__moon-beam ds-main-hero-section__moon-beam--halo" />
            <div className="ds-main-hero-section__moon-disc" />
          </div>

          <div className="ds-main-hero-section__content">
            <div className="ds-main-hero-section__inner">
              <div className="ds-main-hero-section__brand" data-testid="hero-badge">
                <span className="ds-main-hero-section__brand-bar" aria-hidden="true" />
                <span className="ds-main-hero-section__brand-copy">
                  <span className="ds-main-hero-section__brand-line ds-main-hero-section__brand-line--primary font-display">
                    DSITE
                  </span>
                  <span className="ds-main-hero-section__brand-line ds-main-hero-section__brand-line--secondary">
                    SOLUTION
                  </span>
                </span>
              </div>

              <div className="ds-main-hero-section__title-stack">
                <HeroTitleBlock
                  copy={hero.en}
                  lang="en"
                  className="ds-main-hero-section__title font-display"
                  highlightTestId="hero-title-highlight"
                  testId="hero-title-en"
                  opacityVar="--ds-hero-en-title-o"
                />
                <HeroTitleBlock
                  copy={hero.kr}
                  lang="ko"
                  className="ds-main-hero-section__title ds-main-hero-section__title--kr"
                  highlightTestId={undefined}
                  testId="hero-title-kr"
                  opacityVar="--ds-hero-kr-title-o"
                />
              </div>

              <h1 className="sr-only" data-testid="hero-title">
                {hero.en.line1} {hero.en.line2Before}
                {hero.en.line2Highlight}
              </h1>

              <div className="ds-main-hero-section__tagline-stack">
                <HeroTagline
                  items={hero.en.tagline}
                  className="ds-main-hero-section__tagline"
                  testId="hero-subtitle"
                  opacityVar="--ds-hero-en-tagline-o"
                />
                <HeroTagline
                  items={hero.kr.tagline}
                  className="ds-main-hero-section__tagline ds-main-hero-section__tagline--kr"
                  testId="hero-subtitle-kr"
                  opacityVar="--ds-hero-kr-tagline-o"
                />
              </div>

              <div className="ds-main-hero-section__cta-stack">
                <HeroCtaBlock
                  copy={{ lead: hero.en.ctaLead, label: hero.en.ctaLabel }}
                  lang="en"
                  testId="hero-cta-en"
                  opacityVar="--ds-hero-en-cta-o"
                  href={hero.ctaUrl}
                />
                <HeroCtaBlock
                  copy={{ lead: hero.kr.ctaLead, label: hero.kr.ctaLabel }}
                  lang="ko"
                  testId="hero-cta-kr"
                  opacityVar="--ds-hero-kr-cta-o"
                  href={hero.ctaUrl}
                />
              </div>

              {HERO_AI_BAR_ENABLED ? (
                <HeroAiConsultBar wrapClassName="ds-main-hero-section__ai-bar" />
              ) : null}
            </div>

            <div
              className="ds-main-hero-section__scroll-hint"
              style={{ opacity: "var(--ds-hero-hint-o, 1)" }}
            >
              <div className="ds-main-hero-section__scroll-hint-labels">
                <span
                  className="ds-main-hero-section__scroll-hint-text"
                  style={{ opacity: "var(--ds-hero-hint-intro-o, 1)" }}
                >
                  Scroll
                </span>
                <span
                  className="ds-main-hero-section__scroll-hint-text"
                  style={{ opacity: "var(--ds-hero-hint-kr-o, 0)" }}
                >
                  아래로 스크롤
                </span>
                <span
                  className="ds-main-hero-section__scroll-hint-text"
                  style={{ opacity: "var(--ds-hero-hint-exit-o, 0)" }}
                >
                  다음 섹션으로
                </span>
              </div>
              <span className="ds-main-hero-section__scroll-hint-line" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
